import { db } from "@db";
import {
  GNOMUX_ROOT_ID,
  GNOMUX_TRASH_ID,
  type GnomuxDirectory,
  type GnomuxFile,
} from "@db/objects";

export class DuplicateDirectoryNameError extends Error {
  constructor(name: string) {
    super(`An object named "${name}" already exists in this folder`);
    this.name = "DuplicateDirectoryNameError";
  }
}

async function getSiblingByName(
  parentId: string,
  name: string,
): Promise<GnomuxFile | GnomuxDirectory | undefined> {
  return db.objects.where({ parent: parentId, name }).first();
}

export async function createDirectory(
  parentId: string,
  ownerId: string,
  name: string,
): Promise<GnomuxDirectory> {
  return db.transaction("rw", db.objects, async () => {
    const existing = await getSiblingByName(parentId, name);

    if (existing) {
      throw new DuplicateDirectoryNameError(name);
    }

    const directory: GnomuxDirectory = {
      id: crypto.randomUUID(),
      name,
      parent: parentId,
      createdAt: new Date(),
      ownerId,
    };

    await db.objects.add(directory);

    return directory;
  });
}

export async function findOrCreateDirectory(
  parentId: string,
  ownerId: string,
  name: string,
): Promise<GnomuxDirectory> {
  return db.transaction("rw", db.objects, async () => {
    const existing = await getSiblingByName(parentId, name);

    if (existing) {
      if ("type" in existing) {
        throw new Error(
          `An object named "${name}" already exists but is not a directory`,
        );
      }

      return existing;
    }

    const directory: GnomuxDirectory = {
      id: crypto.randomUUID(),
      name,
      parent: parentId,
      createdAt: new Date(),
      ownerId,
    };

    await db.objects.add(directory);

    return directory;
  });
}

export async function createCollection(
  fileIds: string[],
  ownerId: string,
): Promise<{ file: GnomuxFile; sequenceIds: string[] }> {
  return db.transaction("rw", db.objects, async () => {
    const files = await db.objects.bulkGet(fileIds);

    const sequenceIds: string[] = [];
    let commonParent: string | null = null;

    for (const obj of files) {
      if (!obj) continue;

      const file = obj as GnomuxFile;
      if (file.type !== "GnomuxSequence") continue;

      sequenceIds.push(file.content as string);

      if (commonParent === null) {
        commonParent = file.parent;
      } else if (commonParent !== file.parent) {
        commonParent = GNOMUX_ROOT_ID;
      }
    }

    const collection: GnomuxFile = {
      id: crypto.randomUUID(),
      name: "",
      type: "GnomuxSequenceCollection",
      content: sequenceIds,
      isLocked: false,
      parent: commonParent ?? GNOMUX_ROOT_ID,
      createdAt: new Date(),
      ownerId,
    };

    await db.objects.add(collection);

    return { file: collection, sequenceIds };
  });
}

export async function moveObject(
  objectId: string,
  newParentId: string,
): Promise<{ previousParent: string } | null> {
  return db.transaction("rw", db.objects, async () => {
    const obj = await db.objects.get(objectId);
    if (!obj) return null;

    const previousParent = (obj as GnomuxFile).parent;

    await db.objects.update(objectId, { parent: newParentId });

    return { previousParent };
  });
}

export async function renameObject(
  objectId: string,
  newName: string,
): Promise<{ previousName: string } | null> {
  return db.transaction("rw", db.objects, async () => {
    const obj = await db.objects.get(objectId);
    if (!obj) return null;

    const previousName = obj.name;

    await db.objects.update(objectId, { name: newName });

    return { previousName };
  });
}

export async function getObject(
  id: string,
): Promise<GnomuxFile | GnomuxDirectory | undefined> {
  return db.objects.get(id);
}

export async function getObjects(
  ids: string[],
): Promise<(GnomuxFile | GnomuxDirectory | undefined)[]> {
  return db.objects.bulkGet(ids);
}

export async function getActiveObjects(): Promise<
  (GnomuxFile | GnomuxDirectory)[]
> {
  return db.objects.filter((item) => !item.deletedAt).toArray();
}

export async function getTrashedObjects(): Promise<
  (GnomuxFile | GnomuxDirectory)[]
> {
  return db.objects.where("parent").equals(GNOMUX_TRASH_ID).toArray();
}

export async function getChildObjects(
  parentId: string,
): Promise<(GnomuxFile | GnomuxDirectory)[]> {
  return db.objects.where("parent").equals(parentId).toArray();
}

export async function getAllObjects(): Promise<
  (GnomuxFile | GnomuxDirectory)[]
> {
  return db.objects.toArray();
}

export async function getFileByContent(
  contentId: string,
): Promise<GnomuxFile | undefined> {
  return (await db.objects.where("content").equals(contentId).first()) as
    | GnomuxFile
    | undefined;
}

export async function updateFile(
  id: string,
  changes: Partial<GnomuxFile>,
): Promise<number> {
  return db.objects.update(id, changes);
}

export async function isAncestor(
  potentialAncestorId: string,
  potentialDescendantId: string,
): Promise<boolean> {
  if (potentialDescendantId === GNOMUX_ROOT_ID) {
    return false;
  }

  const visited = new Set<string>();
  let currentId = potentialDescendantId;

  while (currentId !== GNOMUX_ROOT_ID) {
    if (visited.has(currentId)) {
      return false;
    }
    visited.add(currentId);

    const obj = await db.objects.get(currentId);
    if (!obj?.parent) break;

    if (obj.parent === potentialAncestorId) {
      return true;
    }
    currentId = obj.parent;
  }

  return false;
}
